import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import i18next from "i18next";
import {
  Button,
  Icon,
  LanguageSwitch,
  PasswordInput,
  TextField,
} from "../../components/design";
import AuthSidePanel from "../../components/Auth/AuthSidePanel";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import "../../components/Auth/AuthForm.css";
import useTranslator from "../../hooks/useTranslator";
import { toast } from "../../helper/common";

// Opened from the landlord welcome email. The link works once and expires
// after 30 days; opening the page does not use it up, saving the password does.
const strongPassword = new RegExp(
  "(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})"
);

const CreatePassword = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  // checking | valid | invalid | error
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({ password: "", confirmPassword: "" });
  const [inputError, setInputError] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
  );

  const handleLanguageChange = async (langVal) => {
    await i18next.changeLanguage(langVal);
    setCurrentLang(langVal);
  };

  const headers = { "Accept-Language": localStorage.getItem("i18nextLng") };

  const checkLink = () => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    setStatus("checking");
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/users/landlord/set-password`, {
        params: { token },
        headers,
      })
      .then((res) => {
        setEmail(res.data.email);
        setStatus("valid");
      })
      .catch((err) => {
        // Only a 400 means the link is invalid or expired. Anything else
        // (server down, network, 500) can be retried.
        setStatus(err?.response?.status === 400 ? "invalid" : "error");
      });
  };

  useEffect(() => {
    checkLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setInputError(_t("passwordDoesNotMatch"));
      return;
    }
    if (!strongPassword.test(form.password)) {
      setInputError(
        _t(
          "pleaseIncludeUppercaseLowercaseAndSpecialCharactersInYourPasswordAndMustBeAtleastEightCharactersLong"
        )
      );
      return;
    }
    setLoading(true);
    await axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/users/landlord/set-password`,
        { token, ...form },
        { headers }
      )
      .then((res) => {
        toast.success(res.data.msg);
        navigate("/auth");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.msg);
      })
      .finally(() => setLoading(false));
  };

  const pill = { icon: "/images/home.svg", label: _t("landlord") };

  let content;
  if (status === "checking") {
    content = null;
  } else if (status === "error") {
    content = (
      <div className="auth-step">
        <AuthStepHeader
          pill={pill}
          title={_t("setYourPassword")}
          subtitle={_t("somethingWentWrong")}
        />
        <Button size="lg" block onClick={checkLink}>
          {_t("tryAgain")}
        </Button>
      </div>
    );
  } else if (status === "invalid") {
    content = (
      <div className="auth-step">
        <AuthStepHeader
          pill={pill}
          title={_t("setYourPassword")}
          subtitle={_t("setPasswordLinkInvalidOrExpired")}
        />
        <Button size="lg" block onClick={() => navigate("/forget-password")}>
          {_t("forgotPassword")}
        </Button>
      </div>
    );
  } else {
    content = (
      <form onSubmit={onSubmit} className="auth-step">
        <AuthStepHeader pill={pill} title={_t("setYourPassword")} />
        <TextField
          id="create-password-email"
          label={_t("emailAddress")}
          name="email"
          icon={<Icon name="mail" />}
          value={email}
          disabled
        />
        <PasswordInput
          name="password"
          value={form.password}
          onChange={onChange}
          inputError={inputError}
          setInputError={setInputError}
          isShowChecks
        />
        <PasswordInput
          name="confirmPassword"
          label={_t("repeatPassword")}
          value={form.confirmPassword}
          onChange={onChange}
          inputError={inputError}
          setInputError={setInputError}
        />
        {inputError && (
          <p className="auth-error" role="alert">
            {inputError}
          </p>
        )}
        <Button type="submit" size="lg" block loading={loading}>
          {_t("setYourPassword")}
        </Button>
      </form>
    );
  }

  // Same layout as the landlord login page (pages/auth/LandlordAuth.tsx)
  return (
    <div className="bigform lanlord-mobile-bg auth-flat">
      <AuthSidePanel role="landlord" />
      <div className="form-right new-form-right overflow-y-scroll">
        <div className="form-inner">
          <div className="logoWrapper">
            <LanguageSwitch
              currentLang={currentLang}
              onChange={handleLanguageChange}
            />
          </div>
          {content}
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;
